import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { OG_IMAGE, SITE_URL, type SeoPageKey } from '../../config/seo';

type SeoHeadProps = {
  page: SeoPageKey;
  noindex?: boolean;
};

const setMeta = (
  name: string,
  content: string,
  attribute: 'name' | 'property' = 'name'
) => {
  let el = document.querySelector(
    `meta[${attribute}="${name}"]`
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }
  el.content = content;
};

const setLink = (rel: string, href: string, hreflang?: string) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
};

export const SeoHead = ({ page, noindex = false }: SeoHeadProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('uk') ? 'uk' : 'en';
  const path = page === 'home' ? '/' : `/${page}`;
  const canonicalUrl = `${SITE_URL}${path}`;
  const title = t(`seo.${page}.title`);
  const description = t(`seo.${page}.description`);
  const keywords = t(`seo.${page}.keywords`);

  useEffect(() => {
    document.documentElement.lang = lang;

    document.title = title;
    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('robots', noindex ? 'noindex,nofollow' : 'index,follow');

    setLink('canonical', canonicalUrl);
    setLink('alternate', `${SITE_URL}${path}`, 'uk');
    setLink('alternate', `${SITE_URL}${path}?lang=en`, 'en');
    setLink('alternate', `${SITE_URL}${path}`, 'x-default');

    setMeta('og:type', 'website', 'property');
    setMeta('og:site_name', t('common.appName'), 'property');
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:image', OG_IMAGE, 'property');
    setMeta('og:image:width', '1200', 'property');
    setMeta('og:image:height', '630', 'property');
    setMeta('og:locale', lang === 'uk' ? 'uk_UA' : 'en_US', 'property');
    setMeta(
      'og:locale:alternate',
      lang === 'uk' ? 'en_US' : 'uk_UA',
      'property'
    );

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', OG_IMAGE);
  }, [canonicalUrl, description, keywords, lang, noindex, path, t, title]);

  return null;
};
